import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";

// WebMCP: expose a few safe site tools to in-browser AI agents via the emerging
// navigator.modelContext API (Chrome WebMCP). Feature-detected → no-op where
// unsupported; tools are unregistered on unmount to avoid ghost invocations.
export const WebMcpProvider = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof navigator === "undefined" || !("modelContext" in navigator)) return;
    const mc = navigator.modelContext;
    if (!mc || typeof mc.registerTool !== "function") return;

    const tools = [
      {
        name: "search_products",
        description:
          "Search KANN's catalog of 3D-printed smart home accessories (mounts, holders and covers for Philips Hue, Google Nest, IKEA TRÅDFRI and more) by keyword.",
        inputSchema: {
          type: "object",
          properties: {
            query: { type: "string", description: "Search keyword, e.g. 'Philips Hue motion sensor mount'." },
          },
          required: ["query"],
        },
        annotations: { readOnlyHint: true },
        execute: async ({ query }) => {
          try {
            const res = await api.get("/products", { params: { q: query } });
            const items = (res.data || []).slice(0, 8).map((p) => ({
              name: p.name,
              price: formatPrice(p.price),
              url: `${window.location.origin}/product/${p.slug || p.id}`,
            }));
            const text = items.length
              ? items.map((i) => `${i.name} — ${i.price} — ${i.url}`).join("\n")
              : `No products found for "${query}".`;
            return { content: [{ type: "text", text }] };
          } catch (e) {
            return { content: [{ type: "text", text: "Product search failed, please try again." }] };
          }
        },
      },
      {
        name: "open_product",
        description: "Open a KANN product page in the browser by its slug (from a search_products result URL).",
        inputSchema: {
          type: "object",
          properties: {
            slug: {
              type: "string",
              description: "Product slug, e.g. 'flushboxx-voor-philips-hue-motion-sensor'.",
            },
          },
          required: ["slug"],
        },
        annotations: { readOnlyHint: false },
        execute: async ({ slug }) => {
          const clean = String(slug || "").replace(/^\/*(product\/)?/, "");
          navigate(`/product/${clean}`);
          return { content: [{ type: "text", text: `Opened product page: /product/${clean}` }] };
        },
      },
    ];

    let active = true;
    (async () => {
      for (const t of tools) {
        if (!active) return;
        try { await mc.registerTool(t); } catch (e) { /* noop */ }
      }
    })();

    return () => {
      active = false;
      if (typeof mc.unregisterTool === "function") {
        for (const t of tools) {
          try { mc.unregisterTool(t.name); } catch (e) { /* noop */ }
        }
      }
    };
  }, [navigate]);

  return null;
};
